import type { TFunction } from 'i18next'
import type { Product, ThresholdNotification } from './types'
import { pagePath } from './routes'
import { dateLocale, formatLongDate } from './months'

/* Pure helpers behind the notification dropdown and the Home notification
   centre (MS-868, ARCHITECTURE_DECISIONS.md §49): the category split, the
   severity colour, the copy and the action set of one notification. They
   port the prototype's `AgentNotifications` store (subscription-portal.html
   `notifIcon`, `entryHTML`, `renderInto`, `fmtNotifTime` and the copy its
   only raise path, `checkThresholds`, writes), with the PO's MS-868 additions
   (100%, Trial, "Trial ends in 7 days", Allow excess). */

/** Who a notification is about: one person's limit (user level) or the
 * account as a whole (everything else, `trial_ending` included) — the
 * prototype's `isUserLevelNotif`. */
export function isUserLevel(n: ThresholdNotification): boolean {
  return n.subjectType === 'end_user'
}

/** The icon colour. Severity, not category, picks it (prototype `notifIcon`):
 * 80% is a heads-up (yellow), 90% and 100% are red. "Trial ends in 7 days" is
 * not a threshold, so it gets its own glyph on the trial banner's yellow
 * (PO Q5). */
export type Severity = 'warning' | 'error' | 'trial'

export function severity(n: ThresholdNotification): Severity {
  if (n.kind === 'trial_ending') return 'trial'
  return (n.threshold ?? 0) >= 90 ? 'error' : 'warning'
}

/** Newest first; equal instants fall back to the (time-sortable) id. */
function byNewest(a: ThresholdNotification, b: ThresholdNotification): number {
  const diff = Date.parse(b.notifiedAt) - Date.parse(a.notifiedAt)
  if (diff !== 0) return diff
  if (a.id === b.id) return 0
  return BigInt(b.id) > BigInt(a.id) ? 1 : -1
}

/** The two categories, account level first, each newest first. Copies, so
 * the query cache is never reordered in place. */
export function splitByLevel(items: readonly ThresholdNotification[]): {
  account: ThresholdNotification[]
  user: ThresholdNotification[]
} {
  return {
    account: items.filter((n) => !isUserLevel(n)).sort(byNewest),
    user: items.filter(isUserLevel).sort(byNewest),
  }
}

/** Whether the row carries its crossing figures. Only a row the previous
 * release wrote during a rollback window lacks them (§49, D1). */
function hasFigures(n: ThresholdNotification): n is ThresholdNotification & { current: string; limit: string } {
  return n.current !== null && n.limit !== null && Number(n.limit) > 0
}

/** The share of the limit the row's own figures show, rounded (prototype
 * `Math.round(p)`); a row without figures reads its threshold (§49, D1). */
export function percentUsed(n: ThresholdNotification): number {
  if (!hasFigures(n)) return n.threshold ?? 0
  return Math.round((Number(n.current) / Number(n.limit)) * 100)
}

/** The prototype's `fmtNotifTs`: local 24-hour HH:MM within the last 24
 * hours, otherwise a short day-and-month date — in the UI language rather
 * than the prototype's hardcoded English months. */
export function formatNotifTime(iso: string, language: string, now: number = Date.now()): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const diff = now - date.getTime()
  if (diff >= 0 && diff < 24 * 3600 * 1000) {
    return new Intl.DateTimeFormat(dateLocale(language), {
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).format(date)
  }
  return new Intl.DateTimeFormat(dateLocale(language), { day: 'numeric', month: 'short' }).format(date)
}

// The copy keys, spelled out so every one is a checked i18n key (no key is
// assembled from a template at runtime).
const USER_BODY = {
  'Mambu MCP': {
    80: 'notifications.user.mcp.body80',
    90: 'notifications.user.mcp.body90',
    100: 'notifications.user.mcp.body100',
  },
  'Mambu Agents': {
    80: 'notifications.user.agents.body80',
    90: 'notifications.user.agents.body90',
    100: 'notifications.user.agents.body100',
  },
} as const

const ACCOUNT_COPY = {
  'Mambu MCP': {
    title: 'notifications.account.mcp.title',
    body: 'notifications.account.mcp.body',
    body100: 'notifications.account.mcp.body100',
    bodyExcess: 'notifications.account.mcp.bodyExcess',
    body100Excess: 'notifications.account.mcp.body100Excess',
    trialTitle: 'notifications.account.trial.mcp.title',
    trialBody: 'notifications.account.trial.mcp.body',
  },
  'Mambu Agents': {
    title: 'notifications.account.agents.title',
    body: 'notifications.account.agents.body',
    body100: 'notifications.account.agents.body100',
    bodyExcess: 'notifications.account.agents.bodyExcess',
    body100Excess: 'notifications.account.agents.body100Excess',
    trialTitle: 'notifications.account.trial.agents.title',
    trialBody: 'notifications.account.trial.agents.body',
  },
} as const satisfies Record<Product, unknown>

export interface NotificationCopy {
  title: string
  /** Null only where the sentence would need a value the row or session
   * lacks (no crossing figures, no account name): the row then shows its
   * title alone rather than invented wording (§49). */
  body: string | null
}

/** Title and body of one notification. User rows follow the prototype's
 * `raiseThreshold` copy (PO Q9 for Agents, Q13 for 100%), on Trial too, where
 * the same user cap is enforced (§48). Account rows follow `raiseAccountVolume` (Q13 100%, Q14 Trial).
 * "Trial ends in 7 days" is Q5's copy. */
export function notificationCopy(
  t: TFunction,
  n: ThresholdNotification,
  context: Readonly<{ accountName: string | null; language: string }>
): NotificationCopy {
  const { accountName, language } = context
  const number = (value: string) => Math.round(Number(value)).toLocaleString(language)

  if (n.kind === 'trial_ending') {
    const body =
      accountName && n.cycleEnd
        ? t('notifications.trialEnding.body', {
            account: accountName,
            product: n.product,
            date: formatLongDate(n.cycleEnd, language),
          })
        : null
    return { title: t('notifications.trialEnding.title'), body }
  }

  const threshold = n.threshold ?? 80
  const pct = percentUsed(n)

  if (isUserLevel(n)) {
    const email = n.subjectId ?? ''
    return {
      title: t('notifications.user.title', { email, threshold }),
      body: t(USER_BODY[n.product][threshold], { email, pct }),
    }
  }

  const copy = ACCOUNT_COPY[n.product]
  const title = n.onTrial ? t(copy.trialTitle, { threshold }) : t(copy.title, { threshold })
  if (!accountName || !hasFigures(n)) return { title, body: null }
  const values = { account: accountName, pct, used: number(n.current), included: number(n.limit) }
  return { title, body: t(accountBodyKey(n, threshold), values) }
}

/** The account body: Trial, then excess on (nothing is blocked, the extra is
 * billed as excess — code review of MR !121), then the excess-off copy. */
function accountBodyKey(n: ThresholdNotification, threshold: number) {
  const copy = ACCOUNT_COPY[n.product]
  if (n.onTrial) return copy.trialBody
  if (n.excessOn) return threshold >= 100 ? copy.body100Excess : copy.bodyExcess
  return threshold >= 100 ? copy.body100 : copy.body
}

export type NotificationActionKey = 'viewLimits' | 'buyMore' | 'allowExcess' | 'viewUsage' | 'viewSubscription'

export interface NotificationAction {
  key: NotificationActionKey
  to: string
  variant: 'primary' | 'ghost'
}

/** The buttons under one notification, in order, each on the notification's
 * own product tab. User rows: View limits. Contracted account rows: Buy more
 * (Subscription, the prototype's `topup/{tab}`), Allow excess (Excess Volume,
 * PO Q6) and View usage; Allow excess is left out when excess was already on
 * (code review of MR !121). A Trial account row and "Trial ends in 7 days"
 * offer neither purchase nor excess (Q14): they point at the Subscription
 * page, where the trial banner and the contracting copy live, and View
 * usage. */
export function notificationActions(n: ThresholdNotification): NotificationAction[] {
  const p = n.product
  if (isUserLevel(n)) return [{ key: 'viewLimits', to: pagePath('user-limits', p), variant: 'primary' }]
  if (n.kind === 'trial_ending' || n.onTrial) {
    return [
      { key: 'viewSubscription', to: pagePath('subscription', p), variant: 'primary' },
      { key: 'viewUsage', to: pagePath('usage', p), variant: 'ghost' },
    ]
  }
  const buyMore: NotificationAction = { key: 'buyMore', to: pagePath('subscription', p), variant: 'primary' }
  const viewUsage: NotificationAction = { key: 'viewUsage', to: pagePath('usage', p), variant: 'ghost' }
  if (n.excessOn) return [buyMore, viewUsage]
  return [buyMore, { key: 'allowExcess', to: pagePath('excess-volume', p), variant: 'ghost' }, viewUsage]
}
